<div class="sample-data-entry-shell" [class.is-loading]="isLoading || measurementsAreLoading">
  @if (isLoading || measurementsAreLoading) {
    <div
      class="loading-overlay"
      data-testid="sample-entry-loading-overlay"
      role="status"
      aria-live="polite"
    >
      <suvo-core-loading-spinner message="Loading sample data..." />
    </div>
  }

  <app-data-entry-check-block-panel>
    <app-header>
      <app-sample-data-entry-sample-switcher />

      <a mat-flat-button routerLink="..">Sample Analysis Overview</a>
    </app-header>

    @if (sample) {
      <section class="row">
        <mat-card>
          <mat-card-header>
            <mat-card-title>Key information</mat-card-title>
          </mat-card-header>
          <mat-card-content class="key-info">
            <div>
              <h3>Tow</h3>
              <p>{{ tow().metadata?.oldTowBaseId ?? tow()._id }}</p>
            </div>
            <div>
              <h3>Sample no.</h3>
              <p>{{ sample.sampleNo }}</p>
            </div>
            <div>
              <h3>Location</h3>
              <p>
                {{ sample.coordinates[1] | coord: 'lat' }}, {{ sample.coordinates[0] | coord: 'lng' }}
              </p>
            </div>
            <div>
              <h3>Midpoint (UTC)</h3>
              <p>{{ sample.sampleMidpointDate | date: 'medium' : 'UTC' }}</p>
            </div>
            <div>
              <h3>Midpoint (Local)</h3>
              <p>{{ getLocaltime() | date: 'medium' : 'UTC' }}</p>
            </div>
            <div>
              <h3>Day/Night</h3>
              <p>{{ getDayNight() }}</p>
            </div>
            <div>
              <h3>PCI colour</h3>
              <app-pci-colour [value]="sample.chlorophyllIndex" />
            </div>
            <div>
              <h3>Factor</h3>
              <p>{{ sample.factor | number }}</p>
            </div>
            <div>
              <h3>Analyst</h3>
              <app-user-avatar [userId]="sample.userId" />
            </div>
            <div>
              <h3>Status</h3>
              <cpr-sample-status [sample]="sample" />
            </div>
          </mat-card-content>
        </mat-card>

        <div style="flex-basis: 25%">
          <app-tow-log-map-tile [sample]="sample" />
        </div>
      </section>
    }

    <section>
      <div class="flex-row">
        <app-sample-details-form [sample]="sample" class="sample-details" />
        <app-data-entry-check-blocks class="checkblocks" />
      </div>
    </section>

    <section [style.--scale]="compactMode() ? 0.75 : 1">
      @if (measurementsAreLoading) {
        <div style="min-height: 1000px">
          <suvo-core-loading-spinner />
        </div>
      } @else {
        <mat-card>
          <mat-card-header class="between" style="gap: var(--global-padding)">
            <mat-card-title>Measurements</mat-card-title>
            <mat-checkbox [(ngModel)]="compactMode">Compact mode</mat-checkbox>
            <button
              mat-flat-button
              (click)="
                sample.isSubmitting || sample.submittedAt ? cancelSubmitting() : submitSample()
              "
              [disabled]="!canSubmitSample"
            >
              {{
                sample.submittedAt
                  ? 'Unsubmit'
                  : sample.isSubmitting
                    ? 'Cancel Submitting'
                    : 'Submit'
              }}
            </button>
          </mat-card-header>

          @if (!canSubmitSample) {
            <div style="padding: 0 var(--global-padding); color: #8c8c8c; font-size: 0.9rem">
              You do not have permission to submit or unsubmit samples.
            </div>
          }

          <mat-card-content>
            <div style="display: flex; justify-content: space-between; gap: 22px">
              <div>
                <span style="opacity: 70%">
                  Use the form below to add measurements to the sample. The measurements that have
                  been added to the sample appear in the left hand panel. Group totals are
                  auto-calculated and displayed as a hierarchy in the right hand panel.
                </span>

                <div style="margin-top: 10px">
                  <mat-button-toggle-group [formControl]="countingMethodCtrl">
                    <mat-button-toggle value="PHYTO">Phyto</mat-button-toggle>
                    <mat-button-toggle value="TRAVERSE">Traverse</mat-button-toggle>
                    <mat-button-toggle value="EYECOUNT">Eyecount</mat-button-toggle>
                  </mat-button-toggle-group>
                </div>
              </div>

              <div style="width: 100%; max-width: 600px; padding-top: 60px">
                <app-abundance-categories [countingMethod]="countingMethod" />
              </div>
            </div>

            <div style="margin: 16px 0">
              @if (sample) {
                <div class="measurement-form-container">
                  @if (!dataEntryEnabled()) {
                    <div style="color: #bdbdbd; text-align: center; margin-bottom: 8px">
                      @if (sample.submittedAt) {
                        Data entry is disabled as this sample is now submitted.
                      } @else if (!sample.userId) {
                        Data entry is currently disabled as this sample has not been allocated.
                      } @else if (sample.userId !== sample.currentUser?._id) {
                        Data entry is disabled as this sample is allocated to a different analyst.
                      }
                      <br />
                      <button
                        *ngIf="!sample.submittedAt"
                        mat-flat-button
                        [checkPermission]="['sample', 'allocate']"
                        (click)="dataEntryEnabled.set(true)"
                        style="margin-top: 12px"
                      >
                        Senior analyst override
                      </button>
                    </div>
                  } @else {
                    <h3 style="text-align: center; margin: 0 0 10px">Add measurement</h3>
                    <app-measurement-form
                      [countingMethod]="countingMethod"
                      [sample]="sample"
                      (measurementAdded)="pull()"
                      style="margin-top: 10px"
                    />
                  }
                </div>
              }
            </div>

            <div fxLayout="row" fxLayoutAlign="center" fxLayoutGap="96px">
              <div>
                <div class="list-heading">
                  <span style="flex-grow: 1"><br />Added Taxon Measurements</span>
                  <span style="padding-right: 12px; text-align: right">
                    Category<br />(Factor Applied)
                  </span>
                  <span style="padding-right: 58px; text-align: right"><br />Count</span>
                </div>
                @for (
                  measurement of processedMeasurements;
                  track measurement.taxon.taxonName + measurement.analysedCount
                ) {
                  @if (measurement.taxon.countingMethod === countingMethod) {
                    <app-measurement-list-item
                      [sample]="sample"
                      [taxon]="measurement.taxon"
                      [measurement]="measurement.measurement"
                      [bracketText]="
                        measurement.other ? (countingMethod === 'PHYTO' ? '' : 'extra') : null
                      "
                      (delete)="pull()"
                      (update)="pull()"
                    />
                  }
                }
                @if (!hasMeasurementsForCountingMethod(countingMethod)) {
                  <div class="note">No taxons have been added for this counting type.</div>
                }
              </div>
              <div>
                <div>
                  <div class="list-heading">
                    <span style="flex-grow: 1"><br />Calculated Taxon Totals</span>
                    <span style="padding-right: 8px; text-align: right"
                      >Category<br />(Factor Applied)</span
                    >
                    <span style="min-width: 40px; text-align: right"><br />Count</span>
                  </div>
                  <app-taxon-hierarchy
                    [sample]="sample"
                    [countingMethod]="countingMethod"
                    [measurements]="measurements ?? []"
                    [showCategories]="true"
                  />
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      }
    </section>
  </app-data-entry-check-block-panel>
</div>
